import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ICONS } from '../components/icons/registry'
import CalendlyButton from '../components/CalendlyButton'
import SEO from '../components/SEO'
import Breadcrumbs from '../components/Breadcrumbs'
import { generateServiceSchema, generateLocalBusinessSchema, generateFAQSchema } from '../lib/structured-data-utils'
import CtaSection from '../components/CtaSection'

const AWSCloudConsultingDubai = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

  const services = [
    {
      id: 'aws-architecture',
      title: 'AWS Architecture Design',
      description: 'Design scalable, secure, and cost-effective AWS architectures tailored to your business needs. We follow AWS Well-Architected Framework principles to build cloud solutions that perform.',
      icon: ICONS.roadmap,
      features: [
        'Well-Architected Framework Reviews',
        'Multi-AZ & Multi-Region Design',
        'Microservices Architecture',
        'Serverless Architecture Design',
        'Data Lake & Analytics Architecture',
        'Disaster Recovery Planning'
      ]
    },
    {
      id: 'aws-migration',
      title: 'AWS Cloud Migration',
      description: 'Migrate your workloads to AWS with minimal risk and downtime. We handle everything from assessment to execution, ensuring a smooth transition to the cloud.',
      icon: ICONS.migration,
      features: [
        'Cloud Readiness Assessment',
        'Migration Strategy (6 Rs)',
        'Database Migration (DMS)',
        'Application Modernization',
        'Hybrid Cloud Setup',
        'Post-Migration Optimization'
      ]
    },
    {
      id: 'aws-devops',
      title: 'AWS DevOps & Automation',
      description: 'Implement DevOps best practices on AWS with infrastructure as code, CI/CD pipelines, and automated operations. We help you move faster and more reliably.',
      icon: ICONS.cicdPipeline,
      features: [
        'AWS CDK & CloudFormation',
        'CodePipeline & CodeBuild',
        'ECS & EKS Container Services',
        'Lambda & Step Functions',
        'Systems Manager Automation',
        'AWS Config & CloudWatch'
      ]
    },
    {
      id: 'aws-optimization',
      title: 'Cost Optimization & FinOps',
      description: 'Reduce your AWS bill while maintaining performance. We implement FinOps practices to give you visibility and control over your cloud spending.',
      icon: ICONS.costOptimisation,
      features: [
        'Cost Analysis & Reporting',
        'Reserved Instance Planning',
        'Savings Plans Optimization',
        'Right-sizing Recommendations',
        'Spot Instance Strategies',
        'Tag-based Cost Allocation'
      ]
    }
  ]

  const awsServices = [
    { name: 'EC2', category: 'Compute' },
    { name: 'Lambda', category: 'Serverless' },
    { name: 'ECS/EKS', category: 'Containers' },
    { name: 'RDS', category: 'Database' },
    { name: 'DynamoDB', category: 'NoSQL' },
    { name: 'S3', category: 'Storage' },
    { name: 'CloudFront', category: 'CDN' },
    { name: 'API Gateway', category: 'API' },
    { name: 'VPC', category: 'Network' },
    { name: 'IAM', category: 'Security' },
    { name: 'CloudWatch', category: 'Monitoring' },
    { name: 'EventBridge', category: 'Events' }
  ]

  const certifications = [
    { name: 'AWS Solutions Architect Professional', level: 'Professional' },
    { name: 'AWS DevOps Engineer Professional', level: 'Professional' },
    { name: 'AWS Security Specialty', level: 'Specialty' },
    { name: 'AWS Database Specialty', level: 'Specialty' }
  ]

  const useCases = [
    {
      title: 'E-commerce Platforms',
      description: 'High-availability architectures for online stores with auto-scaling, caching, and multi-region failover.',
      Icon: ICONS.ecommerce
    },
    {
      title: 'SaaS Applications',
      description: 'Multi-tenant architectures with tenant isolation, usage-based billing, and API management.',
      Icon: ICONS.saas
    },
    {
      title: 'Data & Analytics',
      description: 'Data lakes, ETL pipelines, and real-time analytics with Redshift, Athena, and Kinesis.',
      Icon: ICONS.analytics
    },
    {
      title: 'AI/ML Workloads',
      description: 'Machine learning infrastructure with SageMaker, training pipelines, and model deployment.',
      Icon: ICONS.ai
    },
    {
      title: 'Media & Streaming',
      description: 'Video processing, transcoding, and content delivery with MediaServices and CloudFront.',
      Icon: ICONS.media
    },
    {
      title: 'IoT Solutions',
      description: 'Connected device platforms with IoT Core, Greengrass, and real-time data processing.',
      Icon: ICONS.iot
    }
  ]

  const faqs = [
    {
      question: 'What AWS cloud consulting services do you offer in Dubai?',
      answer: 'We offer comprehensive AWS consulting in Dubai including cloud architecture design following the AWS Well-Architected Framework, cloud migration using the 6 Rs strategy, DevOps automation with CDK and CloudFormation, cost optimization and FinOps, serverless application development with Lambda and API Gateway, and managed AWS infrastructure services. We serve startups, SMEs, and enterprises across the UAE.'
    },
    {
      question: 'How much does AWS cloud consulting cost in Dubai?',
      answer: 'AWS consulting rates in Dubai typically range from AED 600 to AED 1,800 per hour depending on the level of expertise required. A cloud architecture review starts from AED 15,000, migration projects range from AED 50,000 to AED 300,000 depending on workload complexity, and FinOps cost optimization engagements start from AED 20,000. We offer fixed-price projects, time and materials, and monthly retainers.'
    },
    {
      question: 'How long does an AWS cloud migration take?',
      answer: 'AWS migration timelines depend on the number and complexity of workloads. A single application migration can be completed in 2 to 4 weeks. A full data center migration for a mid-size Dubai business typically takes 3 to 6 months. We use a phased approach starting with non-critical workloads to validate the migration strategy before moving production systems, ensuring minimal business disruption.'
    },
    {
      question: 'Can you help reduce our AWS bill?',
      answer: 'Yes, AWS cost optimization is one of our core services. On average we help Dubai businesses reduce their AWS spend by 30 to 40 percent through right-sizing instances, implementing Reserved Instances and Savings Plans, optimizing storage tiers, eliminating unused resources, and implementing auto-scaling policies. Our FinOps engagements include detailed cost analysis, recommendations, and implementation of cost governance practices.'
    },
    {
      question: 'Which AWS region should I use for my Dubai business?',
      answer: 'For Dubai businesses, the AWS Middle East region in Bahrain (me-south-1) is typically the best choice as it offers the lowest latency for UAE users and helps meet data residency requirements. For global applications, we design multi-region architectures using me-south-1 as the primary region with eu-west-1 or ap-south-1 as secondary regions for disaster recovery and global content delivery via CloudFront.'
    },
    {
      question: 'Do you hold AWS certifications?',
      answer: 'Yes, our team holds multiple professional-level AWS certifications including AWS Solutions Architect Professional, AWS DevOps Engineer Professional, AWS Security Specialty, and AWS Database Specialty. These certifications demonstrate deep, validated expertise across architecture, operations, security, and data services on the AWS platform.'
    },
    {
      question: 'What industries do you serve with AWS consulting in Dubai?',
      answer: 'We serve a wide range of industries in Dubai and the UAE including fintech and banking, e-commerce and retail, healthcare and medtech, media and entertainment, logistics and supply chain, SaaS companies, and government organizations. Our AWS solutions are designed to meet industry-specific compliance requirements such as PCI DSS, HIPAA, and UAE data residency regulations.'
    },
    {
      question: 'What is the difference between AWS and other cloud providers like Azure and GCP?',
      answer: 'AWS is the market leader with the broadest service portfolio and the most mature ecosystem, making it ideal for organizations that need a wide range of cloud services. Azure integrates deeply with Microsoft products and is often preferred by enterprises using Windows, Active Directory, and Office 365. GCP excels in data analytics, machine learning, and Kubernetes workloads. We primarily specialize in AWS but can advise on multi-cloud strategies when appropriate.'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  const serviceSchemas = services.map(service => generateServiceSchema({
    name: `${service.title} in Dubai`,
    description: service.description,
    provider: 'Demirix',
    areaServed: 'Dubai, UAE'
  }))

  const localBusinessSchema = generateLocalBusinessSchema()
  const faqSchema = generateFAQSchema(faqs)
  const structuredData = [...serviceSchemas, localBusinessSchema, faqSchema]

  const breadcrumbs = [
    { name: 'Services', path: '/services' },
    { name: 'AWS Cloud Consulting Dubai', path: '/aws-cloud-consulting-dubai' }
  ]

  return (
    <div className="pt-20 bg-white dark:bg-gray-900">
      <SEO
        title="AWS Cloud Consulting Dubai | AWS Migration & Architecture Services UAE"
        description="Expert AWS cloud consulting in Dubai. We design, migrate, and optimize AWS solutions for UAE businesses. AWS architecture, migration, DevOps, and cost optimization services."
        keywords={[
          'aws consulting dubai',
          'aws cloud services uae',
          'aws migration dubai',
          'aws architecture dubai',
          'aws solutions architect dubai',
          'cloud migration uae',
          'aws devops dubai',
          'aws cost optimization',
          'serverless dubai',
          'aws lambda consulting',
          'aws eks dubai',
          'cloud consulting uae'
        ]}
        url={currentUrl}
        structuredData={structuredData}
      />
      <Breadcrumbs customCrumbs={breadcrumbs} />

      {/* Hero Section */}
      <section className="min-h-[80vh] flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium mb-6">
              AWS Partner
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              AWS Cloud Consulting in Dubai
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              Unlock the full potential of Amazon Web Services for your business. We help Dubai
              companies design, migrate, and optimize their AWS infrastructure for maximum
              performance, security, and cost efficiency.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <CalendlyButton className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 w-full sm:w-auto">
                Schedule AWS Consultation
              </CalendlyButton>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto"
              >
                Get a Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-primary-600 dark:bg-primary-700">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              { stat: '100+', label: 'AWS Projects Delivered' },
              { stat: '40%', label: 'Average Cost Savings' },
              { stat: '99.99%', label: 'Uptime Achieved' },
              { stat: 'ME Region', label: 'AWS Expertise' }
            ].map((item, index) => (
              <motion.div key={index} variants={itemVariants} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{item.stat}</div>
                <div className="text-primary-100">{item.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Our AWS Services
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                End-to-end AWS consulting from architecture design to ongoing optimization
                and managed services.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service) => {
                const Icon = service.icon
                return (
                  <motion.div
                    key={service.id}
                    variants={itemVariants}
                    className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
                  >
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 flex-shrink-0" aria-hidden="true">
                        <Icon className="w-full h-full" darkTreatment="none" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300">
                          {service.description}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="w-5 h-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* AWS Services We Work With */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                AWS Services We Work With
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Deep expertise across the AWS service portfolio.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4">
              {awsServices.map((service, index) => (
                <div
                  key={index}
                  className="px-6 py-3 bg-white dark:bg-gray-700 rounded-full border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{service.name}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{service.category}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                AWS Use Cases
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                We build AWS solutions for diverse industries and workloads.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
                >
                  <div className="w-12 h-12 mb-4" aria-hidden="true">
                    <useCase.Icon className="w-full h-full" darkTreatment="none" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {useCase.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {useCase.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                AWS Certifications
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Our team holds professional-level AWS certifications.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4">
              {certifications.map((cert, index) => (
                <div
                  key={index}
                  className="px-6 py-4 bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm"
                >
                  <div className="text-xs text-primary-600 dark:text-primary-400 font-medium mb-1">{cert.level}</div>
                  <div className="font-semibold text-gray-900 dark:text-white">{cert.name}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Common questions about AWS cloud consulting services in Dubai answered by our engineering team.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cross-link Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Related Services
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/devops-services-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                DevOps Services
              </Link>
              <Link
                to="/kubernetes-consulting-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                Kubernetes Consulting
              </Link>
              <Link
                to="/devsecops-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                DevSecOps Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        heading="Ready to Optimize Your AWS Infrastructure?"
        description="Let's discuss how we can help your Dubai business leverage AWS for better performance, reliability, and cost efficiency."
        primary={{ kind: 'calendly', label: 'Schedule Free Consultation' }}
        secondary={{ kind: 'link', label: 'Contact Us', href: '/contact' }}
      />
    </div>
  )
}

export default AWSCloudConsultingDubai
